import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ConexaoWhatsApp from './whatsapp/ConexaoWhatsApp';
import './BotControl.css';
import { firestore } from '../config/firebaseConfig';
import { doc, setDoc, getDoc } from 'firebase/firestore';

const tenantId = localStorage.getItem('tenantId');
const clientId = tenantId || '';

const getAiInstructionScript = async () => {
  try {
    const docRef = doc(firestore, 'inquilinos', clientId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.aiInstructionScript || '';
    }
    return '';
  } catch (error) {
    console.error('Erro ao buscar aiInstructionScript:', error);
    return '';
  }
};

const updateAiInstructionScript = async (script) => {
  if (!clientId) return;
  try {
    await setDoc(
      doc(firestore, 'inquilinos', clientId),
      { aiInstructionScript: script },
      { merge: true }
    );
    console.log('Instrução atualizada com sucesso.');
  } catch (error) {
    console.error('Erro ao atualizar aiInstructionScript:', error);
  }
};

const BotControl = () => {
  const [qrCode, setQrCode] = useState(null);
  const [status, setStatus] = useState('Verificando status...');
  const [messageHistory, setMessageHistory] = useState([]);
  const [isBotConnected, setIsBotConnected] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false); // Trava de segurança
  const [aiInstructionScript, setAiInstructionScript] = useState('');
  
  const wsInstance = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const wsUrl = process.env.REACT_APP_WS_URL;

  const fetchStatus = async () => {
    try {
      const response = await axios.post(`${apiUrl}/bot-status`, { sanitizedClientId: clientId });
      const { status: botStatus } = response.data;
      
      setIsBotConnected(botStatus === 'connected');
      
      if (botStatus === 'disconnected') {
        setMessageHistory([]);
        setQrCode(null);
      }
      
      setStatus(
        botStatus === 'connected' ? 'Bot conectado' :
        botStatus === 'qr' ? 'Aguardando QR Code...' :
        botStatus === 'starting' ? 'Iniciando o bot...' : 'Bot desconectado'
      );
    } catch (error) {
      console.error('Erro ao obter status:', error);
    }
  };

  const setupWebSocket = () => {
    if (!wsUrl || wsInstance.current) return;

    const ws = new WebSocket(`${wsUrl}?tenant=${clientId}`);
    wsInstance.current = ws;

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'qr') setQrCode(`${apiUrl}${data.url}`);
        if (data.type === 'status') {
           if (data.message.includes('conectado')) {
             setIsBotConnected(true);
             setQrCode(null);
           }
        }
        if (data.type === 'messageHistory') setMessageHistory(data.history);
      } catch (e) {
        console.error("Erro no processamento da mensagem WS:", e);
      }
    };

    return () => ws.close();
  };

  useEffect(() => {
    getAiInstructionScript().then(setAiInstructionScript);
    fetchStatus();
    const cleanupWs = setupWebSocket();
    const intervalId = setInterval(fetchStatus, 5000);

    return () => {
      clearInterval(intervalId);
      if (cleanupWs) cleanupWs();
      if (wsInstance.current) wsInstance.current.close();
    };
  }, []);

  const startBot = async () => {
    setIsProcessing(true); // Bloqueia o clique
    try {
      await updateAiInstructionScript(aiInstructionScript);
      setStatus('Iniciando o bot...');
      await axios.post(`${apiUrl}/start-bot`, { sanitizedClientId: clientId, aiInstructionScript });
    } catch (error) {
      setStatus('Erro ao iniciar');
      console.error(error);
    } finally {
      setIsProcessing(false); // Libera o clique após resposta
    }
  };

  const disconnectBot = async () => {
    setIsProcessing(true); // Bloqueia o clique
    try {
      await axios.post(`${apiUrl}/disconnect-bot?sanitizedClientId=${clientId}`);
      setIsBotConnected(false);
      setStatus('Bot desconectado');
      setQrCode(null);
    } catch (error) {
      setStatus('Erro ao desconectar');
    } finally {
      setIsProcessing(false); // Libera o clique após resposta
    }
  };

  return (
    <>
      <ConexaoWhatsApp onMudou={fetchStatus} />
      <div className="bot-control-container">
        <h1>WhatsApp Bot</h1>

        <div className="ai-instruction">
          <label htmlFor="aiInstruction">Instrução do Bot:</label>
          <textarea
            id="aiInstruction"
            value={aiInstructionScript}
            onChange={(e) => setAiInstructionScript(e.target.value)}
            onBlur={() => updateAiInstructionScript(aiInstructionScript)}
            placeholder="Digite a instrução para o bot"
            rows="6"
            style={{ width: '100%' }}
          />
        </div>

        <div className="buttons">
          <button 
            onClick={startBot} 
            disabled={isBotConnected || isProcessing}
          >
            {isProcessing ? 'Processando...' : 'Ligar'}
          </button>
          <button 
            onClick={disconnectBot} 
            disabled={!isBotConnected || isProcessing}
          >
            Desligar
          </button>
        </div>

        <p>Status: {status}</p>

        {qrCode && (
          <div style={{ margin: '20px 0' }}>
            <img src={qrCode} alt="QR Code" style={{ width: '300px', height: '300px' }} />
          </div>
        )}

        <div className="message-history">
          <h2>Histórico de Mensagens</h2>
          <ul>
            {messageHistory.map((msg, index) => (
              <li key={msg.id || index} className={msg.type}>
                <div className="message-header">
                  {msg.contact && (
                    <div className="message-header-info">
                      {msg.contact.profilePicUrl && (
                        <img src={msg.contact.profilePicUrl} alt={msg.contact.name || 'Foto'} className="profile-pic" />
                      )}
                      <div className="contact-info">
                        <strong>{msg.contact.name}</strong>
                        {msg.contact.phoneNumber && <span> ({msg.contact.phoneNumber})</span>}
                      </div>
                    </div>
                  )}
                  <span>{new Date(msg.time).toLocaleString()}</span>
                </div>
                <p>{msg.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
};

export default BotControl;
